/**
 * CurtainModalClassic — Monolithic curtain device modal template.
 *
 * Complete curtain control panel in a single file:
 * - Position slider (0–100%) with open/close icons and current position display
 * - Open / Stop / Close action buttons (3 in a row)
 *
 * Part of the monolithic modal architecture.
 */
import { useState, useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './CurtainModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const CURTAIN_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Position Section ──
  { id: 'position', label: 'Position', icon: 'curtains', slots: [
    { id: 'position-label', label: 'Title "POSITION"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'position-icon-open', label: 'Open End Icon', editableProps: ['fontSize', 'color'] },
    { id: 'position-icon-close', label: 'Close End Icon', editableProps: ['fontSize', 'color'] },
    { id: 'position-value', label: 'Position Percentage Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'position-unit', label: 'Unit "%"', editableProps: ['fontSize', 'color'] },
    { id: 'position-slider', label: 'Position Slider Track', editableProps: ['background', 'borderRadius', 'height'] },
    { id: 'position-slider-fill', label: 'Slider Fill (open portion)', editableProps: ['background', 'borderRadius'] },
    { id: 'position-curtain-left', label: 'Left Curtain Panel Visual', editableProps: ['background', 'borderRadius'] },
    { id: 'position-curtain-right', label: 'Right Curtain Panel Visual', editableProps: ['background', 'borderRadius'] },
  ]},
  // ── Controls Section ──
  { id: 'controls', label: 'Controls', icon: 'open_in_full', slots: [
    { id: 'controls-label', label: 'Title "CONTROLS"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'control-btn', label: 'Control Button (Open/Stop/Close)', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize', 'border', 'glow'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
    { id: 'control-btn-icon', label: 'Control Button Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
    { id: 'control-btn-text', label: 'Control Button Text', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
]

const DEFAULT_SECTION_ORDER = CURTAIN_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONTROLS = [
  { id: 'open',  label: 'Open',  icon: 'curtains' },
  { id: 'stop',  label: 'Stop',  icon: 'stop_circle' },
  { id: 'close', label: 'Close', icon: 'curtains_closed' },
] as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CurtainModalClassic({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const deviceOpenPercent = numAttr(deviceState, 'openPercent', 'position', 'openLevel') ?? 50
  const clampedDevicePercent = Math.max(0, Math.min(100, deviceOpenPercent))
  // Movement state: 'opening' | 'closing' | 'stopped' — derived from device state if available
  const movementState = strAttr(deviceState, 'movementState', 'curtainState', 'motorState')

  // ---- Local UI state ----
  const [pendingPercent, setPendingPercent] = useState<number | null>(null)
  const displayPercent = pendingPercent ?? clampedDevicePercent

  // ---- Commands ----
  const sendControl = useCallback((cmd: string) => {
    if (cmd === 'open') setPendingPercent(100)
    else if (cmd === 'close') setPendingPercent(0)
    if (isEditorMode) return
    if (cmd === 'open') {
      onCommand('open', { openPercent: 100 })
    } else if (cmd === 'close') {
      onCommand('close', { openPercent: 0 })
    } else {
      onCommand('stop')
    }
  }, [isEditorMode, onCommand])

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingPercent(Number(e.currentTarget.value))
  }, [])

  const handleSliderPointerUp = useCallback((e: React.PointerEvent<HTMLInputElement>) => {
    const val = Number(e.currentTarget.value)
    setPendingPercent(val)
    if (isEditorMode) return
    if (val === 100) {
      onCommand('open', { openPercent: 100 })
    } else if (val === 0) {
      onCommand('close', { openPercent: 0 })
    } else {
      onCommand('setPosition', { openPercent: val })
    }
  }, [isEditorMode, onCommand])

  // Active control button: derived from movement state or last command
  const activeControl: string | null = movementState === 'opening' ? 'open'
    : movementState === 'closing' ? 'close'
    : movementState === 'stopped' ? 'stop'
    : null

  // Curtain panel widths: each panel covers half the visual gap
  // openPercent=100 → both panels fully retracted (0% gap fill each)
  // openPercent=0   → both panels meet in the middle (50% each)
  const panelWidth = ((100 - displayPercent) / 2)

  return (
    <div
      className={`curtain-modal-classic${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="curtain-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="curtain-modal-classic__header">
        <button className="curtain-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="curtain-modal-classic__title" data-modal-slot="header-title">{device.name || 'Living Room Curtain'}</h2>
        <button
          className="curtain-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'position':
            return (
              <div key="position" className={`curtain-modal-classic__position-section${hl}`} data-modal-section="position">
                <div className="curtain-modal-classic__position-label" data-modal-slot="position-label">POSITION</div>

                {/* Curtain visual: two panels sliding from left and right */}
                <div className="curtain-modal-classic__curtain-visual">
                  <div
                    className="curtain-modal-classic__curtain-panel curtain-panel--left"
                    data-modal-slot="position-curtain-left"
                    style={{ width: `${panelWidth}%` }}
                  />
                  <div className="curtain-modal-classic__curtain-gap" />
                  <div
                    className="curtain-modal-classic__curtain-panel curtain-panel--right"
                    data-modal-slot="position-curtain-right"
                    style={{ width: `${panelWidth}%` }}
                  />
                </div>

                {/* Percentage display with open/close icons */}
                <div className="curtain-modal-classic__position-row">
                  <span className="material-symbols-rounded curtain-modal-classic__position-icon" data-modal-slot="position-icon-open">curtains_closed</span>
                  <div className="curtain-modal-classic__position-display">
                    <span className="curtain-modal-classic__position-value" data-modal-slot="position-value">{displayPercent}</span>
                    <span className="curtain-modal-classic__position-unit" data-modal-slot="position-unit">%</span>
                  </div>
                  <span className="material-symbols-rounded curtain-modal-classic__position-icon" data-modal-slot="position-icon-close">curtains</span>
                </div>

                {/* Slider */}
                <div className="curtain-modal-classic__slider-wrap">
                  <div
                    className="curtain-modal-classic__slider-track"
                    data-modal-slot="position-slider"
                  >
                    <div
                      className="curtain-modal-classic__slider-fill"
                      data-modal-slot="position-slider-fill"
                      style={{ width: `${displayPercent}%` }}
                    />
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={displayPercent}
                    className="curtain-modal-classic__slider-input"
                    aria-label="Curtain open position"
                    onChange={handleSliderChange}
                    onPointerUp={handleSliderPointerUp}
                  />
                </div>
              </div>
            )
          case 'controls':
            return (
              <div key="controls" className={`curtain-modal-classic__controls-section${hl}`} data-modal-section="controls">
                <div className="curtain-modal-classic__controls-label" data-modal-slot="controls-label">CONTROLS</div>
                <div className="curtain-modal-classic__controls-grid">
                  {CONTROLS.map((ctrl) => {
                    const isActive = activeControl === ctrl.id
                    return (
                      <button
                        key={ctrl.id}
                        className={`curtain-modal-classic__control-btn${isActive ? ' active' : ''}`}
                        data-modal-slot="control-btn"
                        data-modal-slot-state={isActive ? 'active' : 'default'}
                        onClick={() => sendControl(ctrl.id)}
                        aria-label={ctrl.label}
                      >
                        <span
                          className="material-symbols-rounded"
                          data-modal-slot="control-btn-icon"
                          data-modal-slot-state={isActive ? 'active' : 'default'}
                        >
                          {ctrl.icon}
                        </span>
                        <span
                          data-modal-slot="control-btn-text"
                          data-modal-slot-state={isActive ? 'active' : 'default'}
                        >
                          {ctrl.label}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
